// BUILD-042 (Oct 16) — Form Library "Set up signing": the expanded-fields editor for an ordinary uploaded PDF.
// (Replaces the Oct 15 one-signature-one-date screen. Setups published with that screen still open here as a signature and a date.)
//
// HR sees the REAL pages of the stored PDF and places, on any page, as many of these as the form needs for the ONE employee who will sign:
// signatures, initials (once, on selected or every page, with exclusions and single-page moves), signing dates, text boxes (single line;
// several lines are shown as "not confirmed" until a real signing test proves them), dropdowns, checkboxes and single-choice groups.
// Values can come from the employee, from the employee record (name / work email only), from fixed text, or be typed by HR when the form is
// assigned. A fillable PDF's own fields can be imported and reviewed; anything that cannot be carried over is named, and a REQUIRED or
// calculated one must be replaced by a real field before publishing.
//
// Saved ONCE per document version; every later assignment reuses it and freezes its own prefilled values. The server is the authority (it
// re-reads the stored PDF and re-validates everything); this screen only keeps HR from building something the server would refuse.
// Tax, direct-deposit and other confidential forms are supported: what the employee types stays in the protected signed document and the
// HR document record. Nothing is copied into employee-profile fields, payroll or tax filings.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getHrSigningSetup, saveHrFormSetup, retireHrSigningConfig, importHrSigningFields, saveHrSigningDraft, discardHrSigningDraft,
  type HrSigningSetup, type HrImportPreview, type HrImportResolution, type HrFormSetupStored,
} from '../../api/client';
import { base64ToBytes, loadPdf, type LoadedPdf, type RenderedPage } from './pdfPageRenderer';
import * as M from './formFieldsModel';
import type { UiField, UiInitialsSet, FieldKind, Box } from './formFieldsModel';
import { FieldProperties, InitialsProperties } from './FieldProperties';
import { ImportReview } from './ImportReview';
import { formatLocalDate } from './hrFormat';

/** Fallback / minimum drawing width. The real width follows the document pane (half the dialog on a wide screen), see pageCssWidth. */
const PAGE_CSS_WIDTH = 620;
const PAGE_CSS_MIN = 420;
const PAGE_CSS_MAX = 1100;
const KIND_STYLE: Record<FieldKind, string> = {
  signature: 'border-[#4AABDE] bg-[#4AABDE]/15 text-[#1d6f9f]',
  initial: 'border-violet-500 bg-violet-500/15 text-violet-800',
  dateSigned: 'border-emerald-500 bg-emerald-500/15 text-emerald-800',
  text: 'border-amber-500 bg-amber-400/15 text-amber-900',
  dropdown: 'border-cyan-600 bg-cyan-500/15 text-cyan-900',
  checkbox: 'border-rose-500 bg-rose-500/15 text-rose-800',
  radio: 'border-fuchsia-500 bg-fuchsia-500/15 text-fuchsia-800',
};
const SAMPLE: Record<string, string> = { signature: 'Sample Employee', initial: 'SE', dateSigned: '10/16/2026' };

type Selection = { type: 'field'; key: string } | { type: 'set'; key: string } | null;
type DragTarget = { kind: 'field'; key: string; fieldKind: FieldKind } | { kind: 'ini'; setKey: string; page: number };
type DragState = { target: DragTarget; mode: 'move' | 'resize'; startX: number; startY: number; orig: Box };

/** Stable text form of the import-review answers, so a change to them counts as an unsaved change too. */
const resKey = (r: Record<string, HrImportResolution>): string => JSON.stringify(Object.entries(r).sort(([a], [b]) => a.localeCompare(b)));

function humanize(name: string): string {
  const last = name.split('.').pop() || name;
  const t = last.replace(/\[\d+\]/g, '').replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').trim();
  return (t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Field').slice(0, 100);
}
const resolutionsFromLive = (live: HrSigningSetup['liveConfig']): Record<string, HrImportResolution> => ({ ...(live?.importReport?.resolutions ?? {}) });

export function SigningSetupPanel({
  templateId, versionId, templateName, versionNumber, onClose, onChanged,
}: {
  templateId: string;
  versionId: string;
  templateName: string;
  versionNumber: number;
  onClose: () => void;
  /** Called after a publish / turn-off so the Form Library can refresh its status chips. */
  onChanged: () => void;
}) {
  const [setup, setSetup] = useState<HrSigningSetup | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pdf, setPdf] = useState<LoadedPdf | null>(null);
  const [preparedPdf, setPreparedPdf] = useState<LoadedPdf | null>(null);
  const [preparedState, setPreparedState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [preparedError, setPreparedError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [rendered, setRendered] = useState<RenderedPage | null>(null);
  const [fields, setFields] = useState<UiField[]>([]);
  const [sets, setSets] = useState<UiInitialsSet[]>([]);
  const [selected, setSelected] = useState<Selection>(null);
  const [importPreview, setImportPreview] = useState<HrImportPreview | null>(null);
  const [resolutions, setResolutions] = useState<Record<string, HrImportResolution>>({});
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState<'publish' | 'retire' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retireConfirm, setRetireConfirm] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState(false);
  // Oct 17: the saved, UNPUBLISHED draft (revision/time/author only; its content is loaded into `fields`/`sets`). `draftBusy` = a save or discard is running.
  const [draftMeta, setDraftMeta] = useState<{ revision: number; savedAt: string; savedByName: string | null; liveChangedSince: boolean } | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const [preparedDiag, setPreparedDiag] = useState<{ code: string; detail: string } | null>(null);
  // Right-hand tabs (Oct 17): 'setup' = published/import/history, 'add' = palette + the selected field's properties, 'fields' = everything on the form.
  const [tab, setTab] = useState<'setup' | 'add' | 'fields' | null>(null);
  const [paneEl, setPaneEl] = useState<HTMLDivElement | null>(null);
  const [pageCssWidth, setPageCssWidth] = useState(PAGE_CSS_WIDTH);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drag = useRef<DragState | null>(null);
  const pdfRef = useRef<LoadedPdf | null>(null);
  const preparedRef = useRef<LoadedPdf | null>(null);
  /** What the editor last SAVED or loaded (the saved draft when there is one, otherwise the published setup): "unsaved changes" is measured against it. */
  const baselineRef = useRef<{ fields: UiField[]; initialsSets: UiInitialsSet[] }>({ fields: [], initialsSets: [] });
  const baselineResRef = useRef<string>('[]');
  /** The published setup only: "this is already the published setup" is measured against it, never against a draft. */
  const publishedRef = useRef<{ fields: UiField[]; initialsSets: UiInitialsSet[] }>({ fields: [], initialsSets: [] });
  /** Newest load wins. React can run the load effect twice (and a slow load can finish after a newer one); a superseded load changes nothing. */
  const loadSeq = useRef(0);

  const eligibility = setup ? (setup.expanded?.eligibility ?? setup.eligibility) : null;
  const rules = setup?.expanded?.rules ?? null;
  const pages = eligibility?.pages ?? [];
  const pageCount = eligibility?.pageCount ?? 0;
  const page = pages[pageNo - 1];
  const live = setup?.liveConfig ?? null;
  const retired = !!setup?.template.retiredAt;
  const previewing = mode === 'preview';
  const activePdf = previewing && preparedPdf ? preparedPdf : pdf;
  // Until the user picks a tab: a fillable PDF opens on Setup & import (that is the first job), anything else opens on Add fields.
  const activeTab: 'setup' | 'add' | 'fields' = tab ?? (importPreview?.fillable ? 'setup' : 'add');
  function goPreview() {
    setMode('preview'); setSelected(null); setAttested(false);
    const first = all.find((f) => f.kind === 'signature')?.page ?? 1; setPageNo(first);
  }

  // ── Load the setup (and the exact stored PDF) ──────────────────────────────────────────────────────────────────────
  const load = useCallback(async (keepEdits: boolean) => {
    const seq = ++loadSeq.current;
    const stale = () => seq !== loadSeq.current;
    setLoadError(null);
    try {
      const s = await getHrSigningSetup(templateId, versionId);
      if (stale()) return;
      setSetup(s);
      const elig = s.expanded?.eligibility ?? s.eligibility;
      const b64 = s.expanded?.fileBase64 ?? s.fileBase64;
      if (!keepEdits) {
        const lf = s.expanded?.liveFields ?? [];
        const ls = s.liveConfig && s.liveConfig.placementSchemaVersion === 3 ? (s.liveConfig.placement as HrFormSetupStored).initialsSets : [];
        publishedRef.current = M.toApiBody(lf, ls);
        const d = s.draft ?? null;
        if (d) {
          // A saved draft resumes exactly where it was left: every authored field, position, type, source, option and required flag.
          const df = d.body.fields as UiField[]; const ds = d.body.initialsSets as UiInitialsSet[];
          setFields(df); setSets(ds);
          baselineRef.current = M.toApiBody(df, ds);
          setResolutions({ ...d.body.importResolutions });
          baselineResRef.current = resKey(d.body.importResolutions);
          setDraftMeta({ revision: d.revision, savedAt: d.savedAt, savedByName: d.savedByName, liveChangedSince: d.liveChangedSince });
        } else {
          setFields(lf); setSets(ls);
          baselineRef.current = M.toApiBody(lf, ls);
          const r = resolutionsFromLive(s.liveConfig);
          setResolutions(r);
          baselineResRef.current = resKey(r);
          setDraftMeta(null);
        }
        setDiscardConfirm(false);
        setSelected(null); setMode('edit'); setAttested(false); setPageNo(1);
      }
      if (elig.ok && b64) {
        setPdf(null);
        pdfRef.current?.destroy(); pdfRef.current = null;
        const loaded = await loadPdf(base64ToBytes(b64));
        if (stale()) { loaded.destroy(); return; }
        pdfRef.current = loaded;
        setPdf(loaded); setPdfError(null);
      } else {
        setPdf(null); pdfRef.current?.destroy(); pdfRef.current = null;
      }
      // A fillable PDF: read its fields (read-only; writes nothing) so the review and the publish checks have the report.
      if (elig.ok && ((elig as { fillableFieldCount?: number }).fillableFieldCount ?? 0) > 0) {
        try { const ip = await importHrSigningFields(templateId, versionId); if (!stale()) setImportPreview(ip); }
        catch (e: any) { if (!stale()) { setImportPreview(null); setLoadError(e?.message || 'The fillable fields of this PDF could not be read.'); } }
      } else setImportPreview(null);
    } catch (e: any) {
      if (!stale()) setLoadError(e?.message || 'Could not open this form for signing setup.');
    }
  }, [templateId, versionId]);
  useEffect(() => { void load(false); return () => { loadSeq.current++; pdfRef.current?.destroy(); pdfRef.current = null; preparedRef.current?.destroy(); preparedRef.current = null; }; }, [load]);

  // ── The PREPARED copy shown in the preview step (fillable PDFs only; flat PDFs are sent as they are) ───────────────
  useEffect(() => {
    if (mode !== 'preview' || !importPreview?.fillable) { setPreparedPdf(null); setPreparedState('idle'); return; }
    let cancelled = false;
    (async () => {
      setPreparedState('loading'); setPreparedError(null); setPreparedDiag(null);
      try {
        const keep = [...new Set(fields.filter((f) => f.origin?.kind === 'imported' && f.origin.acroName).map((f) => f.origin!.acroName as string))];
        const p = await importHrSigningFields(templateId, versionId, keep);
        if (!p.preparedBase64) throw Object.assign(new Error(p.preparedProblem || 'The prepared copy could not be built.'), { diag: p.preparedDiagnostic ?? null });
        const loaded = await loadPdf(base64ToBytes(p.preparedBase64));
        if (cancelled) { loaded.destroy(); return; }
        preparedRef.current?.destroy(); preparedRef.current = loaded;
        setPreparedPdf(loaded); setPreparedState('ready');
      } catch (e: any) {
        if (!cancelled) { setPreparedState('error'); setPreparedError(e?.message || 'The prepared copy could not be built.'); setPreparedDiag(e?.diag ?? null); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ── Draw the current page ───────────────────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (!activePdf || !canvasRef.current) return;
    setRendered(null);
    activePdf.renderPage(pageNo, canvasRef.current, pageCssWidth)
      .then((r) => { if (!cancelled) { setRendered(r); setPdfError(null); } })
      .catch((e: any) => { if (!cancelled && e?.name !== 'RenderingCancelledException') setPdfError(e?.message || 'This page could not be drawn.'); });
    return () => { cancelled = true; };
  }, [activePdf, pageNo, pageCssWidth]);

  // ── Page width follows the document pane (Oct 17 Owner request: about half the dialog for the document, half for the controls) ──
  useEffect(() => {
    if (!paneEl) return;
    const measure = () => {
      const w = Math.round((paneEl.clientWidth - 44) / 10) * 10;   // minus the grey frame, padding and a scrollbar; 10 px steps avoid redraw churn
      setPageCssWidth(Math.max(PAGE_CSS_MIN, Math.min(PAGE_CSS_MAX, w)));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(paneEl);
    return () => ro.disconnect();
  }, [paneEl]);

  // ── Derived state ───────────────────────────────────────────────────────────────────────────────────────────────────
  const body = useMemo(() => M.toApiBody(fields, sets), [fields, sets]);
  const problems = useMemo(() => (rules ? M.setupProblems(fields, sets, pages, { multilineConfirmed: rules.multilineConfirmed, maxFields: rules.maxFields }) : []), [fields, sets, pages, rules]);
  const unresolved = useMemo(() => M.unresolvedImportItems(importPreview?.report ?? null, fields, resolutions), [importPreview, fields, resolutions]);
  const sameAsPublished = M.sameBody(body, publishedRef.current) && !!live && live.placementSchemaVersion === 3;
  // Unsaved changes = anything that differs from what was last saved/loaded: the fields, the initials rules or the import-review answers.
  const dirty = !M.sameBody(body, baselineRef.current) || resKey(resolutions) !== baselineResRef.current;
  const canEdit = !!setup && !!eligibility?.ok && !retired && !!pdf && !!rules;
  const all = useMemo(() => M.allFields(fields, sets, pageCount), [fields, sets, pageCount]);
  const importedNames = useMemo(() => new Set(fields.filter((f) => f.origin?.kind === 'imported' && f.origin.acroName).map((f) => f.origin!.acroName as string)), [fields]);
  const hasUnimported = !!importPreview?.draftFields.some((d) => d.origin?.acroName && !importedNames.has(d.origin.acroName));
  const summary = useMemo(() => M.summarize(fields, sets, pageCount), [fields, sets, pageCount]);
  const selField = selected?.type === 'field' ? fields.find((f) => f.fieldKey === selected.key) ?? null : null;
  const selSet = selected?.type === 'set' ? sets.find((s) => s.setKey === selected.key) ?? null : null;
  const group = selField?.kind === 'radio' && selField.radio ? fields.filter((f) => f.radio?.groupKey === selField.radio!.groupKey) : [];
  const ctxNow = (): M.NewFieldContext => ({ page: pageNo, pages, fields, sets });
  const edit = () => { setMessage(null); setAttested(false); };

  // ── Adding / changing / removing ────────────────────────────────────────────────────────────────────────────────────
  function addKind(kind: FieldKind) {
    if (!canEdit || !page) return;
    edit();
    if (kind === 'initial') { const s = M.newInitialsSet(ctxNow()); setSets((x) => [...x, s]); setSelected({ type: 'set', key: s.setKey }); return; }
    if (kind === 'radio') { const g = M.newRadioGroup(ctxNow()); setFields((x) => [...x, ...g]); setSelected({ type: 'field', key: g[0].fieldKey }); return; }
    const f = M.newField(kind, ctxNow()); setFields((x) => [...x, f]); setSelected({ type: 'field', key: f.fieldKey });
  }
  const updateField = (next: UiField) => { edit(); setFields((fs) => fs.map((f) => (f.fieldKey === next.fieldKey ? next : f))); };
  const updateSet = (next: UiInitialsSet) => { edit(); setSets((ss) => ss.map((s) => (s.setKey === next.setKey ? next : s))); };
  function removeField(key: string) { edit(); setFields((fs) => fs.filter((f) => f.fieldKey !== key)); setSelected(null); }
  function removeGroup(groupKey: string) { edit(); setFields((fs) => fs.filter((f) => f.radio?.groupKey !== groupKey)); setSelected(null); }
  function changeGroup(patch: { required?: boolean; groupKey?: string }) {
    if (!selField?.radio) return; edit();
    const g = selField.radio.groupKey;
    setFields((fs) => fs.map((f) => (f.radio?.groupKey === g ? { ...f, ...(patch.required !== undefined ? { required: patch.required } : {}), ...(patch.groupKey ? { radio: { ...f.radio!, groupKey: patch.groupKey } } : {}) } : f)));
  }
  function addOption() {
    if (!selField?.radio || group.length === 0) return; edit();
    const opt = M.addRadioOption(group, ctxNow()); setFields((fs) => [...fs, opt]); setSelected({ type: 'field', key: opt.fieldKey });
  }
  function importAll() {
    if (!importPreview) return; edit();
    const keys = M.usedKeys(fields, sets);
    const fresh = importPreview.draftFields.filter((d) => d.origin?.acroName && !importedNames.has(d.origin.acroName));
    const added = fresh.map((d) => { const k = keys.has(d.fieldKey) ? M.uniqueKey(d.fieldKey, keys) : (keys.add(d.fieldKey), d.fieldKey); return { ...d, fieldKey: k }; });
    setFields((fs) => [...fs, ...added]);
    setMessage(`${added.length} field${added.length === 1 ? '' : 's'} imported. Review each one, then add anything the import could not carry over.`);
  }
  function replaceItem(itemId: string) {
    const it = importPreview?.report?.items.find((i) => i.id === itemId);
    if (!it || !it.box || !it.page) return; edit();
    const keys = M.usedKeys(fields, sets);
    const key = M.uniqueKey(`${it.acroName.split('.').pop() || 'field'}_entry`, keys);
    const [minW, minH] = M.KIND_MIN.text;
    const f: UiField = {
      fieldKey: key, kind: 'text', page: it.page, box: { ...it.box, width: Math.max(it.box.width, minW), height: Math.max(it.box.height, minH) }, label: humanize(it.acroName),
      required: it.requiredInPdf || it.calculated, source: 'employee', text: { multiline: false, maxLength: rules?.defaultTextLength ?? 100, validation: 'none' }, origin: { kind: 'authored', note: `replaces ${it.acroName}` },
    };
    setFields((fs) => [...fs, f]); setResolutions((r) => ({ ...r, [itemId]: { kind: 'replaced', fieldKey: key } }));
    setSelected({ type: 'field', key }); setPageNo(it.page); setTab('add');   // the new box's properties (required, format) are on the Add fields tab
  }
  const ackItem = (itemId: string) => { edit(); setResolutions((r) => ({ ...r, [itemId]: { kind: 'ack' } })); };

  // ── Drag / resize / keyboard ────────────────────────────────────────────────────────────────────────────────────────
  function applyBox(t: DragTarget, box: Box) {
    if (t.kind === 'field') { setFields((fs) => fs.map((f) => (f.fieldKey === t.key ? { ...f, box } : f))); return; }
    setSets((ss) => ss.map((s) => {
      if (s.setKey !== t.setKey) return s;
      const same = box.x === s.anchor.x && box.y === s.anchor.y && box.width === s.anchor.width && box.height === s.anchor.height;
      const others = s.overrides.filter((o) => o.page !== t.page);
      return { ...s, overrides: same ? others : [...others, { page: t.page, box }] };
    }));
  }
  const targetKind = (t: DragTarget): FieldKind => (t.kind === 'field' ? t.fieldKind : 'initial');
  function onPointerDown(e: React.PointerEvent, target: DragTarget, orig: Box, mode2: 'move' | 'resize') {
    if (previewing || !canEdit) return;
    e.preventDefault(); e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { target, mode: mode2, startX: e.clientX, startY: e.clientY, orig };
    setSelected(target.kind === 'field' ? { type: 'field', key: target.key } : { type: 'set', key: target.setKey });
    setTab('add');   // clicking a field on the page shows its properties
  }
  function onPointerMove(e: React.PointerEvent) {
    const d = drag.current;
    if (!d || !rendered || !page) return;
    const dx = (e.clientX - d.startX) / rendered.scale; const dy = (e.clientY - d.startY) / rendered.scale;
    edit();
    applyBox(d.target, d.mode === 'move' ? M.moveBox(d.orig, dx, dy, page) : M.resizeBox(d.orig, dx, dy, page, targetKind(d.target)));
  }
  function onPointerUp(e: React.PointerEvent) {
    if (!drag.current) return;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* already released */ }
    drag.current = null;
  }
  function onKeyDown(e: React.KeyboardEvent, target: DragTarget, box: Box) {
    if (previewing || !canEdit || !page) return;
    if ((e.key === 'Delete' || e.key === 'Backspace') && target.kind === 'field') { e.preventDefault(); removeField(target.key); return; }
    if (!e.key.startsWith('Arrow')) return;
    e.preventDefault(); edit();
    applyBox(target, M.nudgeBox(box, e.key, e.shiftKey, e.ctrlKey || e.metaKey, page, targetKind(target)));
  }

  // ── Initials per-page actions ───────────────────────────────────────────────────────────────────────────────────────
  function resetPage(setKey: string, p: number) { edit(); setSets((ss) => ss.map((s) => (s.setKey === setKey ? { ...s, overrides: s.overrides.filter((o) => o.page !== p) } : s))); }
  function applyToAll(setKey: string, p: number) {
    edit();
    setSets((ss) => ss.map((s) => {
      if (s.setKey !== setKey) return s;
      const box = s.overrides.find((o) => o.page === p)?.box ?? s.anchor;
      return { ...s, anchor: { ...box }, overrides: [] };
    }));
  }

  // ── Publish / turn off ─────────────────────────────────────────────────────────────────────────────────────────────
  const publishBlocked = problems.length > 0 || unresolved.length > 0 || (importPreview?.fillable && preparedState !== 'ready');
  async function publish() {
    if (busy || publishBlocked || !attested || (sameAsPublished && !!live)) return;
    setBusy('publish'); setError(null); setMessage(null);
    try {
      const ids = new Set((importPreview?.report?.items ?? []).map((i) => i.id));
      const importResolutions = Object.fromEntries(Object.entries(resolutions).filter(([id]) => ids.has(id)));
      const r = await saveHrFormSetup(templateId, versionId, { ...body, reviewAttested: true, importResolutions });
      setMessage(r.message);
      onChanged();
      await load(false);
    } catch (e: any) {
      setError(e?.message || 'Could not publish the form setup.');
    } finally { setBusy(null); }
  }
  async function turnOff() {
    if (busy) return;
    setBusy('retire'); setError(null); setMessage(null);
    try {
      const r = await retireHrSigningConfig(templateId, versionId);
      setMessage(r.message); setRetireConfirm(false);
      onChanged();
      await load(false);
    } catch (e: any) {
      setError(e?.message || 'Could not turn off the signing setup.');
    } finally { setBusy(null); }
  }
  // ── Save draft / Save and close / discard (Oct 17) ──────────────────────────────────────────────────────────────────
  // A draft is explicit, durable and SEPARATE from publishing: it keeps an unfinished or currently unpublishable setup (every field, position,
  // type, source, option and required flag) across closing the screen, a browser reload and a server restart. It is not a published setup,
  // never changes one, and never affects forms that are already assigned. A save that fails leaves this screen and every edit exactly as it was.
  async function saveDraft(closeAfter: boolean): Promise<boolean> {
    if (draftBusy || busy || !canEdit) return false;
    const savedBody = M.toApiBody(fields, sets); const savedRes = resKey(resolutions);
    setDraftBusy(true); setError(null); setMessage(null);
    try {
      const r = await saveHrSigningDraft(templateId, versionId, { ...savedBody, importResolutions: resolutions, expectedRevision: draftMeta?.revision ?? null });
      baselineRef.current = savedBody; baselineResRef.current = savedRes;
      setDraftMeta({ revision: r.draft.revision, savedAt: r.draft.savedAt, savedByName: r.draft.savedByName, liveChangedSince: r.draft.liveChangedSince });
      setCloseConfirm(false);
      if (closeAfter) { onClose(); return true; }
      setMessage(r.message);
      return true;
    } catch (e: any) {
      setError(e?.message || 'The draft could not be saved. Everything on this screen is still here; try again.');
      return false;
    } finally { setDraftBusy(false); }
  }
  async function discardSavedDraft() {
    if (draftBusy || busy) return;
    setDraftBusy(true); setError(null); setMessage(null);
    try {
      const r = await discardHrSigningDraft(templateId, versionId);
      await load(false);
      setMessage(r.message);
    } catch (e: any) {
      setError(e?.message || 'The draft could not be discarded. It is still saved.');
    } finally { setDraftBusy(false); setDiscardConfirm(false); }
  }
  function requestClose() {
    // With unsaved changes the screen never closes by itself: the three choices below are the only ways out (a second Escape/✕ changes nothing).
    if (dirty) { setCloseConfirm(true); return; }
    onClose();
  }
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') requestClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ── Page overlays ───────────────────────────────────────────────────────────────────────────────────────────────────
  function sampleFor(f: UiField): string {
    if (f.kind === 'signature' || f.kind === 'initial' || f.kind === 'dateSigned') return SAMPLE[f.kind];
    if (f.kind === 'checkbox') return '☐';
    if (f.kind === 'radio') return `○ ${f.radio?.optionValue ?? ''}`;
    if (f.source === 'profile') return f.profileKey === 'employeeWorkEmail' ? '[work email]' : '[employee name]';
    if (f.source === 'assignment') return f.text?.validation === 'dateUS' ? '[MM/DD/YYYY entered by HR]' : '[HR enters at assignment]';
    if (f.source === 'fixed') return f.kind === 'dropdown' ? (f.fixedValue ?? '') : f.text?.validation === 'dateUS' && f.fixedValue ? f.fixedValue : '[fixed text]';
    if (f.kind === 'dropdown') return f.dropdown?.defaultValue ?? 'Choose ▾';
    return '';
  }
  function renderOverlay(f: UiField, target: DragTarget, selectedNow: boolean) {
    if (!rendered || !page) return null;
    const css = M.boxToCss(f.box, page, rendered.scale);
    const testId = target.kind === 'field' ? `field-${f.fieldKey}` : `ini-${target.setKey}-${target.page}`;
    return (
      <div
        key={testId}
        data-testid={testId}
        role="group"
        tabIndex={previewing ? -1 : 0}
        aria-label={`${M.KIND_LABEL[f.kind]} "${f.label}" on page ${f.page}. ${previewing ? '' : 'Drag to move, arrow keys to nudge, Shift plus arrows to resize.'}`}
        onPointerDown={(e) => onPointerDown(e, target, f.box, 'move')}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={(e) => onKeyDown(e, target, f.box)}
        onFocus={() => { if (!previewing) { setSelected(target.kind === 'field' ? { type: 'field', key: target.key } : { type: 'set', key: target.setKey }); setTab('add'); } }}
        style={{ position: 'absolute', left: css.left, top: css.top, width: css.width, height: css.height, touchAction: 'none', cursor: previewing ? 'default' : 'move', zIndex: selectedNow && !previewing ? 5 : undefined }}
        className={`border-2 ${previewing ? 'border-dashed border-gray-500 bg-white/60 text-blue-900' : KIND_STYLE[f.kind]} ${selectedNow && !previewing ? 'ring-2 ring-offset-1 ring-amber-400' : ''} ${f.required ? '' : 'border-opacity-60'} rounded-sm text-[10px] leading-tight select-none`}
      >
        {/* The label is clipped by THIS wrapper so the box itself can carry the resize grip on its corner (Oct 17). */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="px-1 truncate">
            {previewing ? <span className={f.kind === 'signature' ? 'italic text-[12px]' : ''}>{sampleFor(f)}</span> : <span className="font-semibold">{f.label}{f.required ? ' *' : ''}</span>}
          </div>
          {previewing && <span className="absolute top-0 right-0.5 text-[8px] text-red-600 font-semibold">SAMPLE</span>}
        </div>
        {/*
          Resize grip (Oct 17 Owner request). The old 10 x 10 white square sat inside the box and, on a checkbox or a narrow text box, was
          mistaken for a checkbox. It is replaced by a clearly different control that exists ONLY for the selected field: a 24 x 24 px
          (accessible target size) rounded handle with diagonal grip lines, straddling the bottom-right corner so it never hides the field.
          Keyboard users resize with Shift + arrow keys on the field (see the field's aria-label); the grip itself is pointer-only.
        */}
        {!previewing && selectedNow && (
          <span
            data-testid={`resize-${testId}`} aria-hidden="true" title="Drag to resize"
            onPointerDown={(e) => onPointerDown(e, target, f.box, 'resize')} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
            style={{ position: 'absolute', right: -10, bottom: -10, width: 24, height: 24, cursor: 'nwse-resize', touchAction: 'none' }}
            className="flex items-center justify-center rounded-md border border-[#1d6f9f] bg-white text-[#1d6f9f] shadow-md hover:bg-sky-50"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" focusable="false" aria-hidden="true">
              <path d="M12.5 4.5 4.5 12.5M12.5 8.5 8.5 12.5M12.5 0.5 0.5 12.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
            </svg>
          </span>
        )}
      </div>
    );
  }
  const pageFields = fields.filter((f) => f.page === pageNo);
  const pageInitials = sets.flatMap((s) => M.expandInitialsSet(s, pageCount).filter((f) => f.page === pageNo).map((f) => ({ s, f })));

  const orderedFields = [...fields].sort((a, b) => a.page - b.page || b.box.y - a.box.y || a.box.x - b.box.x);
  const prefilledList = all.filter((f) => f.source !== 'employee');
  const fillable = (importPreview?.fillable ?? false);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2" role="dialog" aria-modal="true" aria-labelledby="signing-setup-title" data-testid="signing-setup-panel">
      {/* Oct 17 Owner request: almost the full screen, so the document and the form controls each get about half of it. */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-lg w-full max-w-[1900px] h-[97vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="signing-setup-title" className="text-sm font-semibold text-gray-800">Set up signing and fields — {templateName} <span className="font-normal text-gray-400">v{versionNumber}</span></h2>
            <p className="text-xs text-gray-500 mt-0.5">One-time setup for this file. Copies assigned after you publish reuse it; copies already assigned keep the setup they were created with.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canEdit && !previewing && (
              <button type="button" onClick={goPreview} disabled={problems.length > 0 || unresolved.length > 0}
                className="px-3 py-1 rounded bg-[#4AABDE] text-white text-xs font-medium disabled:opacity-40" data-testid="go-preview"
                title={problems.length > 0 || unresolved.length > 0 ? 'Fix the items in the amber list first' : 'Check how the form will look, then publish it'}>
                Preview and publish
              </button>
            )}
            {canEdit && previewing && (
              <>
                <button type="button" onClick={() => setMode('edit')} className="px-2.5 py-1 rounded border border-gray-300 text-xs font-medium text-gray-800 hover:bg-gray-50" data-testid="back-to-place">← Back to editing</button>
                <button type="button" onClick={publish} disabled={!attested || busy !== null || !!publishBlocked || (sameAsPublished && !!live)}
                  className="px-3 py-1 rounded bg-[#4AABDE] text-white text-xs font-medium disabled:opacity-40" data-testid="publish-signing"
                  title={!attested ? 'Tick the review box on the right first' : undefined}>
                  {busy === 'publish' ? 'Publishing…' : live ? 'Publish new setup' : 'Publish form setup'}
                </button>
              </>
            )}
            {canEdit && (
              <>
                <button type="button" onClick={() => void saveDraft(false)} disabled={!dirty || draftBusy || busy !== null}
                  className="px-2.5 py-1 rounded border border-gray-300 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40" data-testid="save-draft"
                  title="Keeps your work as a draft. It is not published and changes nothing for employees.">
                  {draftBusy ? 'Saving…' : 'Save draft'}
                </button>
                <button type="button" onClick={() => (dirty ? void saveDraft(true) : onClose())} disabled={draftBusy || busy !== null}
                  className="px-2.5 py-1 rounded border border-gray-300 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-40" data-testid="save-and-close">
                  Save and close
                </button>
              </>
            )}
            <button type="button" onClick={requestClose} className="text-sm text-gray-500 hover:text-gray-800 px-2" aria-label="Close signing setup" data-testid="close-signing-setup">✕</button>
          </div>
        </div>

        {closeConfirm && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-center gap-3 flex-wrap" role="alert" data-testid="close-confirm">
            <span>You have changes that are not saved.{draftMeta ? ' Your earlier saved draft stays as it is unless you save.' : ''}</span>
            <button type="button" className="underline font-semibold disabled:opacity-40" onClick={() => void saveDraft(true)} disabled={draftBusy || !canEdit} data-testid="confirm-save-and-close">{draftBusy ? 'Saving…' : 'Save and close'}</button>
            <button type="button" className="underline" onClick={() => setCloseConfirm(false)} data-testid="keep-editing">Keep editing</button>
            <button type="button" className="underline text-red-800" onClick={onClose} disabled={draftBusy} data-testid="discard-and-close">Discard unsaved changes</button>
            {error && <span className="basis-full text-red-800" role="alert">{error}</span>}
          </div>
        )}

        <div className="overflow-y-auto flex-1">
          {loadError && <div className="p-5 text-sm text-red-700" role="alert">{loadError}</div>}
          {!setup && !loadError && <div className="p-5 text-sm text-gray-400">Opening the form…</div>}

          {setup && eligibility && !eligibility.ok && (
            <div className="p-5 space-y-3">
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="setup-problems" role="alert">
                <div className="font-semibold mb-1">This file can't be set up for electronic signing.</div>
                <ul className="list-disc ml-5 space-y-1">{eligibility.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
              </div>
              <p className="text-xs text-gray-600">Nothing was changed. The form keeps the paper workflow (print, sign, upload). To use electronic signing, replace the form with a corrected PDF — that becomes a new version you can set up.</p>
            </div>
          )}

          {setup && eligibility?.ok && rules && (
            <div className="p-4 grid gap-4 lg:grid-cols-2 lg:items-start">
              {/* ── The real page ──
                  Oct 17 (Owner request): the form and the field controls are TWO separate scroll areas on a wide screen, so scrolling the
                  PDF down to its bottom never moves the field list (and the other way round). The page controls stay pinned at the top
                  of the form's own area. On a narrow screen everything stacks and the whole dialog scrolls as before. */}
              <div ref={setPaneEl} className="lg:max-h-[calc(97vh-8rem)] lg:overflow-y-auto lg:pr-1" data-testid="pdf-scroll-pane">
                <div className="sticky top-0 z-10 bg-white py-1 flex items-center gap-2 mb-2 text-xs text-gray-600 flex-wrap">
                  <button type="button" onClick={() => setPageNo((n) => Math.max(1, n - 1))} disabled={pageNo <= 1} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40" data-testid="page-prev" aria-label="Previous page">←</button>
                  <span data-testid="page-indicator" aria-live="polite">Page {pageNo} of {pageCount}</span>
                  <button type="button" onClick={() => setPageNo((n) => Math.min(pageCount, n + 1))} disabled={pageNo >= pageCount} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40" data-testid="page-next" aria-label="Next page">→</button>
                  <span className="ml-2" data-testid="field-count">{summary.total} field{summary.total === 1 ? '' : 's'} on this form</span>
                  {previewing && fillable && <span className="text-emerald-700" data-testid="showing-prepared">{preparedState === 'ready' ? 'Showing the prepared copy the employee will receive' : preparedState === 'loading' ? 'Building the prepared copy…' : ''}</span>}
                </div>
                <div className="overflow-auto border border-gray-200 rounded bg-gray-100 p-2">
                  <div style={{ position: 'relative', width: pageCssWidth, margin: '0 auto' }} data-testid="signing-page">
                    <canvas ref={canvasRef} data-testid="signing-page-canvas" className="block bg-white shadow" aria-label={`Page ${pageNo} of the form`} />
                    {rendered && pageFields.map((f) => renderOverlay(f, { kind: 'field', key: f.fieldKey, fieldKind: f.kind }, selected?.type === 'field' && selected.key === f.fieldKey))}
                    {rendered && pageInitials.map(({ s, f }) => renderOverlay(f, { kind: 'ini', setKey: s.setKey, page: pageNo }, selected?.type === 'set' && selected.key === s.setKey))}
                    {!rendered && !pdfError && <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">Drawing page…</div>}
                  </div>
                </div>
                {pdfError && <div className="mt-2 text-xs text-red-700" role="alert">{pdfError} Signing cannot be set up for this file in this browser.</div>}
                {fillable && !previewing && <p className="mt-2 text-[11px] text-gray-500">This page shows the original PDF. The employee receives a prepared copy of it (shown in the preview step) in which the PDF's own fields are replaced by the fields you set up here.</p>}
              </div>

              {/* ── Controls ──
                  Oct 17 (Owner request: "way too much scrolling, too many boxes"): ONE fixed-height column. A slim status strip, a short
                  problems bar and three tabs; only the open tab scrolls. "Preview and publish" lives in the header beside Save draft. */}
              <div className="flex flex-col gap-2 text-xs text-gray-700 min-h-0 lg:h-[calc(97vh-8rem)]" data-testid="fields-scroll-pane">
                {retired && <div className="rounded border border-gray-200 bg-gray-50 p-2 text-gray-600">This form is retired, so its signing setup can't be changed. Reactivate the form first.</div>}

                {/* status strip: published setup, saved draft, unsaved changes - one line each */}
                <div className="space-y-1" data-testid="draft-status">
                  {live ? (
                    <div data-testid="live-config" className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-900">
                      <span className="font-medium">Published:</span> Setup {live.configNumber} is live for this version ({formatLocalDate(live.createdAt)}){live.placementSchemaVersion === 3 ? `, ${live.fieldCount ?? summary.total} fields` : ', signature and date only'}. New assignments are pinned to it.
                      {live.placementSchemaVersion !== 3 && <span className="ml-1 text-emerald-800">It opens here as a signature and a date; add what else the form needs and publish to replace it for future assignments.</span>}
                      {(setup?.expanded?.liveNeedsCorrection?.length ?? 0) > 0 && (
                        <span className="block mt-0.5 text-amber-900" data-testid="live-needs-correction">
                          <span className="font-medium">Needs review before it can be used again:</span> this published setup was saved before values set by HR were made read-only for the employee ({setup!.expanded!.liveNeedsCorrection!.length} field{setup!.expanded!.liveNeedsCorrection!.length === 1 ? '' : 's'}). It stays as it is for existing assignments and signed documents, but it cannot be assigned or sent. Choose what each field should be below, then publish a new setup.
                        </span>
                      )}
                    </div>
                  ) : (
                    <div data-testid="no-live-config" className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-gray-600"><span className="font-medium">Published:</span> nothing yet. This version uses the paper workflow until you publish.</div>
                  )}
                  {canEdit && draftMeta && (
                    <div className="rounded border border-sky-200 bg-sky-50 px-2 py-1 text-sky-900" data-testid="draft-banner">
                      <span className="font-medium">Saved draft — not published.</span> Saved {formatLocalDate(draftMeta.savedAt)}{draftMeta.savedByName ? ` by ${draftMeta.savedByName}` : ''}; nothing changes for employees until you publish.
                      {draftMeta.liveChangedSince && <span className="ml-1 text-amber-800" data-testid="draft-live-changed">A different setup was published after this draft was saved; check it before publishing.</span>}
                      {!discardConfirm ? (
                        <button type="button" className="ml-2 underline" onClick={() => setDiscardConfirm(true)} disabled={draftBusy || busy !== null} data-testid="discard-draft">Discard draft</button>
                      ) : (
                        <span className="ml-2 inline-flex gap-2 items-center flex-wrap" role="alert">
                          <span>Remove it{live ? ' and go back to the published setup' : ''}? Unsaved edits are lost too.</span>
                          <button type="button" className="underline" onClick={() => setDiscardConfirm(false)}>Keep it</button>
                          <button type="button" className="underline font-semibold text-red-800" onClick={() => void discardSavedDraft()} disabled={draftBusy} data-testid="confirm-discard-draft">{draftBusy ? 'Discarding…' : 'Discard draft'}</button>
                        </span>
                      )}
                    </div>
                  )}
                  {canEdit && dirty && <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-900" data-testid="unsaved-note">You have unsaved changes. “Save draft” keeps them without publishing.</div>}
                </div>

                {message && <div className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-900" role="status" data-testid="setup-message">{message}</div>}
                {error && <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-red-800" role="alert" data-testid="setup-error">{error}</div>}

                {canEdit && !previewing && (
                  <>
                    {(problems.length > 0 || unresolved.length > 0) && (
                      <ul className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-900 list-disc pl-5 space-y-0.5 max-h-24 overflow-y-auto" data-testid="placement-problems">
                        {problems.slice(0, 6).map((p, i) => <li key={`p${i}`}>{p}</li>)}
                        {problems.length > 6 && <li>…and {problems.length - 6} more.</li>}
                        {unresolved.length > 0 && <li>{unresolved.length} imported item{unresolved.length === 1 ? '' : 's'} still need{unresolved.length === 1 ? 's' : ''} a decision (see the Setup &amp; import tab).</li>}
                      </ul>
                    )}

                    <div role="tablist" aria-label="Signing setup sections" className="flex gap-1 border-b border-gray-200 shrink-0" data-testid="setup-tabs">
                      {([
                        ['setup', 'Setup & import', unresolved.length > 0 ? `${unresolved.length}!` : null],
                        ['add', 'Add fields', null],
                        ['fields', 'All fields', String(summary.total)],
                      ] as Array<['setup' | 'add' | 'fields', string, string | null]>).map(([id, label, badge]) => (
                        <button key={id} type="button" role="tab" aria-selected={activeTab === id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
                          className={`px-3 py-1.5 -mb-px border-b-2 text-xs font-medium ${activeTab === id ? 'border-[#4AABDE] text-[#1d6f9f]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                          {label}{badge ? <span className={`ml-1 rounded-full px-1.5 text-[10px] ${badge.endsWith('!') ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{badge}</span> : null}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3" role={canEdit && !previewing ? 'tabpanel' : undefined} data-testid="tab-panel">
                  {canEdit && !previewing && activeTab === 'add' && (
                    <>
                      <div className="rounded border border-gray-200 p-2.5 space-y-1.5">
                        <div className="font-semibold text-gray-800">Add what the employee completes</div>
                        <p className="text-gray-500">Go to the page you want, then add each item. It appears on that page; drag it into place, and drag the corner grip to resize.</p>
                        <div className="flex gap-1.5 flex-wrap" data-testid="palette">
                          {M.KIND_ORDER.map((k) => (
                            <button key={k} type="button" onClick={() => addKind(k)} data-testid={`add-${k}`}
                              className={`px-2 py-1 rounded border text-[11px] font-medium bg-white hover:bg-gray-50 ${KIND_STYLE[k].split(' ')[0]}`}>
                              + {k === 'radio' ? 'Choice group' : k === 'initial' ? 'Initials (repeat)' : M.KIND_LABEL[k]}
                            </button>
                          ))}
                        </div>
                      </div>
                      {(selField || selSet) ? (
                        <div className="rounded border border-gray-200 p-2.5 bg-gray-50">
                          {selField && <FieldProperties field={selField} group={group} rules={rules!} onChange={updateField} onChangeGroup={changeGroup} onAddOption={addOption} onDelete={() => removeField(selField.fieldKey)} onDeleteGroup={() => selField.radio && removeGroup(selField.radio.groupKey)} />}
                          {selSet && <InitialsProperties set={selSet} pageCount={pageCount} selectedPage={pageNo} onChange={updateSet} onDelete={() => { edit(); setSets((ss) => ss.filter((s) => s.setKey !== selSet.setKey)); setSelected(null); }} onSelectPage={(p) => setPageNo(p)} onResetPage={(p) => resetPage(selSet.setKey, p)} onApplyToAll={(p) => applyToAll(selSet.setKey, p)} />}
                        </div>
                      ) : (
                        <p className="text-gray-400 px-1">Click a field on the page, or add one above, to set its label, whether it is required, and where its value comes from.</p>
                      )}
                    </>
                  )}

                  {canEdit && !previewing && activeTab === 'fields' && (
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">Everything on this form</div>
                      {orderedFields.length === 0 && sets.length === 0 ? <div className="text-gray-400">Nothing added yet.</div> : (
                        <ul className="space-y-0.5" data-testid="field-list">
                          {sets.map((s) => (
                            <li key={s.setKey}>
                              <button type="button" onClick={() => { setSelected({ type: 'set', key: s.setKey }); setTab('add'); const p = M.initialsPageList(s, pageCount)[0]; if (p) setPageNo(p); }} className={`text-left w-full px-1.5 py-0.5 rounded hover:bg-gray-100 ${selected?.type === 'set' && selected.key === s.setKey ? 'bg-amber-50' : ''}`} data-testid={`list-set-${s.setKey}`}>
                                <span className="font-medium">{s.label}</span> — initials on pages {M.formatPageList(M.initialsPageList(s, pageCount)) || 'none'}{s.required ? ' · required' : ''}
                              </button>
                            </li>
                          ))}
                          {orderedFields.map((f) => (
                            <li key={f.fieldKey}>
                              <button type="button" onClick={() => { setSelected({ type: 'field', key: f.fieldKey }); setTab('add'); setPageNo(f.page); }} className={`text-left w-full px-1.5 py-0.5 rounded hover:bg-gray-100 ${selected?.type === 'field' && selected.key === f.fieldKey ? 'bg-amber-50' : ''}`} data-testid={`list-${f.fieldKey}`}>
                                <span className="font-medium">{f.label || f.fieldKey}</span> <span className="text-gray-500">— p{f.page} · {M.describeField(f)}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {((canEdit && !previewing && activeTab === 'setup') || !canEdit) && (
                    <>
                      {canEdit && (importPreview?.fillable && importPreview.report ? (
                        <ImportReview
                          report={importPreview.report} importedKeys={importedNames} resolutions={resolutions} unresolved={unresolved} busy={!!busy} hasUnimported={hasUnimported}
                          onImportAll={importAll} onReplace={replaceItem} onAck={ackItem} onGoTo={(p) => setPageNo(p)}
                        />
                      ) : (
                        <p className="text-gray-500 rounded border border-gray-200 p-2.5">This PDF has no fillable fields to import. Use the Add fields tab to mark what the employee signs, dates and fills in.</p>
                      ))}

                      {live && !retired && (
                        <div>
                          {!retireConfirm ? (
                            <button type="button" className="underline text-red-700" onClick={() => setRetireConfirm(true)} data-testid="retire-signing">Turn off signing for new assignments</button>
                          ) : (
                            <div className="rounded border border-red-200 bg-red-50 p-2 text-red-800 space-y-1.5">
                              <div>Turn off electronic signing for this version? New assignments will use the paper workflow. Forms already assigned keep their setup and can still be signed.</div>
                              <div className="flex gap-3">
                                <button type="button" className="underline" onClick={() => setRetireConfirm(false)}>Cancel</button>
                                <button type="button" className="underline font-semibold" onClick={turnOff} disabled={busy !== null} data-testid="confirm-retire-signing">{busy === 'retire' ? 'Turning off…' : 'Turn off'}</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <details data-testid="signing-setup-history" className="rounded border border-gray-200 px-2.5 py-1.5">
                        <summary className="cursor-pointer font-semibold text-gray-800">History ({setup.history.length})</summary>
                        {setup.history.length === 0 ? <div className="text-gray-400 mt-1">No setups published yet.</div> : (
                          <ul className="space-y-1 mt-1">
                            {setup.history.map((h) => (
                              <li key={h.configId} className="text-gray-600">
                                <span className="font-medium">Setup {h.configNumber}</span> — {h.retiredAt ? 'no longer used for new assignments' : 'live'};{' '}
                                {h.placementSchemaVersion === 3 ? `${h.fieldCount ?? '?'} fields${h.hasPreparedCopy ? ' (prepared copy kept)' : ''}` : 'signature and date only'}.
                                <div className="text-gray-400">Published {formatLocalDate(h.createdAt)}{h.createdByName ? ` by ${h.createdByName}` : ''}{h.retiredAt ? `; replaced or turned off ${formatLocalDate(h.retiredAt)}` : ''}.</div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </details>
                    </>
                  )}

                  {canEdit && previewing && (
                    <div className="space-y-2" data-testid="preview-step">
                      <div className="font-semibold text-gray-800">Preview</div>
                      <p className="text-gray-500">This is where each item will appear for the employee. The text shown is a sample only{fillable ? '; the page is the prepared copy that will actually be sent' : ''}.</p>
                      {fillable && preparedState === 'error' && (
                        <div className="rounded border border-red-200 bg-red-50 p-2 text-red-800" role="alert" data-testid="prepared-error">
                          <div>{preparedError} Publishing is not possible until this is fixed.</div>
                          {preparedDiag && <details className="mt-1" data-testid="prepared-diagnostic"><summary className="cursor-pointer underline">Technical details</summary><div className="mt-0.5 font-mono break-all">{preparedDiag.code}: {preparedDiag.detail}</div></details>}
                          <div className="mt-1">Nothing you set up is lost. {dirty ? '“Save draft” keeps it.' : 'It is saved.'}</div>
                        </div>
                      )}
                      <div className="rounded border border-gray-200 bg-gray-50 p-2" data-testid="preview-summary">
                        <div className="font-medium text-gray-800">{summary.total} fields · {summary.required} required · {summary.prefilled} filled in ahead of time · {summary.locked} locked</div>
                        <div className="text-gray-600">
                          {Object.entries(summary.byKind).map(([k, n]) => `${n} ${M.KIND_LABEL[k as FieldKind].toLowerCase()}${n === 1 ? '' : 's'}`).join(' · ')}
                        </div>
                        {prefilledList.length > 0 && (
                          <ul className="mt-1 text-gray-600 list-disc ml-4">
                            {prefilledList.map((f) => <li key={f.fieldKey}>{f.label} (p{f.page}): {M.SOURCE_LABEL[f.source].toLowerCase()}{f.locked ? ', locked' : ''}</li>)}
                          </ul>
                        )}
                      </div>
                      <div className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-900">
                        <label className="flex items-start gap-2">
                          <input type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} className="mt-0.5" data-testid="attest-review" />
                          <span>I reviewed every field on every page: its position, label, whether it is required and where its value comes from. Answers the employee enters stay in the protected signed document and this form's HR record; nothing is copied into employee-profile fields, payroll or tax filings. Then use <b>{live ? 'Publish new setup' : 'Publish form setup'}</b> at the top.</span>
                        </label>
                      </div>
                      {sameAsPublished && live && <p className="text-gray-500">This is already the published setup.</p>}
                      <p className="text-gray-500">Publishing applies to forms assigned from now on. Forms already assigned keep their original setup and values.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
